/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import handleEvalReportError, { resolveServerErrorMessage } from './evalErrors';
import notifyRubrics from './rubricsNotifications';
import { buildRubricsSchedulerPath } from './rubricsApiPaths';

/**
 * PUT /rubrics/:rubricId/scheduler — the schedule instant plus the sample percent and
 * scoring mode, all of which the endpoint requires together. A past instant is rejected server-side
 * for one-time runs, so the form validates that before publishing.
 */
export default class RubricsSchedulerService extends BaseService {
  constructor() {
    super(constants.SAVE_RUBRICS_SCHEDULER, 'RubricsSchedulerStatus');
  }

  async handleEvent(eventName, event) {
    const { graphId, rubricId, tenantId, requestBody } = event;
    const headers = utils.buildHeadersWithTenantId({ tenantId });
    const url = buildRubricsSchedulerPath({ rubricId, graphId });
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').put(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const store = Framework.getStoreRegistry().getStore('rubricsData');

    if (data?.success) {
      notifyRubrics({ type: 'success', langMessage: 'rubrics.SCHEDULE_SUCCESS' });
      store.completeScheduler();
      return;
    }

    notifyRubrics({ type: 'error', message: resolveServerErrorMessage(data), langMessage: 'rubrics.SCHEDULE_ERROR' });
    store.failScheduler();
  }

  handleError(error) {
    Framework.getStoreRegistry().getStore('rubricsData').failScheduler();
    handleEvalReportError(error, 'rubrics.SCHEDULE_ERROR');
  }
}
